import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FunctionComponent } from 'react';

import Button from '../../generic/Button';
import { TextInput } from '../../generic/Forms';
import Icon, { icons, IconText } from '../../generic/Icon';
import Toggle from '../../generic/Toggle';
import Tooltip from '../../generic/Tooltip';
import type { JiraCustomField } from '../../../../shared/jira';

type JiraProjectSettingsCustomFieldsHeaderItemProps = {
  heading: string;
  tooltip?: string;
};

/**
 * Represents one of the "header" cells in the Custom Fields table.
 */
const JiraProjectSettingsCustomFieldsHeaderItem: FunctionComponent<JiraProjectSettingsCustomFieldsHeaderItemProps> = ({
  heading,
  tooltip,
}) => (
  <div className="jira-custom-fields__header__item">
    {heading}
    {tooltip && [
      ' ',
      (
        <Tooltip key="tooltip" tooltip={tooltip}>
          <span className="inline-block">
            <Icon icon={icons.infoCircle} />
          </span>
        </Tooltip>
      ),
    ]}
  </div>
);

type JiraProjectSettingsCustomFieldsRowProps = JiraCustomField & {
  isDisabled: boolean;
  onUpdateField: (field: Partial<JiraCustomField>) => void;
  onDelete: () => void;
};

/**
 * Represents a single "row" in the custom fields "table".
 */
const JiraProjectSettingsCustomFieldsRow: FunctionComponent<JiraProjectSettingsCustomFieldsRowProps> = ({
  onUpdateField,
  onDelete,
  isDisabled,
  ...props
}) => (
  <div className="jira-custom-fields__row">
    <TextInput
      label="Field ID"
      placeholder="10000"
      size="small"
      value={props.fieldId}
      onChange={fieldId => onUpdateField({ fieldId })}
      isDisabled={isDisabled}
    />
    <TextInput
      label="Field Display Name"
      placeholder="Approvers"
      size="small"
      value={props.displayName}
      onChange={displayName => onUpdateField({ displayName })}
      isDisabled={isDisabled}
    />
    <TextInput
      label="Default Value"
      placeholder="optional"
      size="small"
      value={props.defaultValue}
      onChange={defaultValue => onUpdateField({ defaultValue })}
      isDisabled={isDisabled}
    />
    <div>
      <Toggle
        icons={false}
        checked={props.isRequired}
        onChange={isRequired => onUpdateField({ isRequired })}
        isDisabled={isDisabled}
      />
    </div>
    <div>
      <Button
        type="outline"
        size="medium"
        onClick={onDelete}
        icon={icons.trash}
        isDisabled={isDisabled}
      />
    </div>
  </div>
);

/**
 * Factory for creating a new custom field.
 */
const newCustomField = (): JiraCustomField => ({
  fieldId: '',
  displayName: '',
  defaultValue: '',
  isRequired: true,
});

/**
 * Maps a custom field (from an Array<JiraCustomField>) back to
 * a tuple entry which can be passed to Object.fromEntries.
 */
const mapFieldToEntry = (field: JiraCustomField) => [field.fieldId, field];

/**
  * Test if a field is empty or not.
  * Note that this is only meant to catch fully empty fields.
  * Invalid inputs are caught on the backend.
  */
const isFieldUnfilled = (field: JiraCustomField) =>
  !field.fieldId.trim?.() && !field.displayName.trim?.();

/**
  * Sets the state value if there are fields, otherwise creates an "empty" row.
  */
const getInitialFieldsState = (fields: JiraCustomField[]): JiraCustomField[] =>
  fields.length > 0 ? fields : [];

type JiraCustomFieldsProps = {
  customFields: Record<string, JiraCustomField>;
  isDisabled: boolean;
  onChange: (fields: Record<string, JiraCustomField>) => void;
  activeJiraId?: number; // When this changes, the local state will be updated to ensure that the latest customFields are used.
};

/**
 * JiraCustomFields is a generic component for managing
 * jira custom fields, used in both the integration settings page
 * and the project settings page.
 */
const JiraCustomFields: FunctionComponent<JiraCustomFieldsProps> = ({
  customFields,
  isDisabled,
  onChange,
  activeJiraId,
}) => {
  //  We store a local copy of the fields to work on so that
  //  we can have an empty row added if there are no fields on mount.
  //  This way, it isn't marked as an "addition", and if the user never
  //  touches the Custom Fields section, the column will be empty for them (expected).
  const fieldValues = useMemo(() => Object.values(customFields || {}), [customFields]);
  const [fields, setFields] = useState(getInitialFieldsState(fieldValues));

  //  Reset the local state when changing between active/editing Jiras.
  useEffect(() => {
    setFields(
      getInitialFieldsState(Object.values(customFields || {})),
    );
  }, [activeJiraId]);

  //  Centralized call for all updates.
  const onUpdateFields = useCallback((doUpdate: (prevState: JiraCustomField[]) => JiraCustomField[]) =>
    setFields(prevFields => {
      //  Get the next state locally
      const nextFields = doUpdate(prevFields);
      //  Then update it in the parent.
      onChange(
        Object.fromEntries(
          nextFields
            .filter(field => !isFieldUnfilled(field))
            .map(mapFieldToEntry),
        ),
      );
      return nextFields;
    }), [onChange]);

  const onAddNewCustomField = useCallback(() =>
    onUpdateFields(prevFields => prevFields.concat(newCustomField())), [onUpdateFields]);

  const getOnDeleteCustomField = useCallback((fieldIndex: number) => () =>
    onUpdateFields(prevFields => prevFields.filter((_field, index) => index !== fieldIndex)), [onUpdateFields]);

  const getOnUpdateField = useCallback((fieldIndex: number) => (updatedField: Partial<JiraCustomField>) =>
    onUpdateFields(prevFields => prevFields.map((field, index) => index !== fieldIndex
      ? field
      : {
        ...field,
        ...updatedField,
      })), [onUpdateFields]);

  return (
    <div className="jira-custom-fields">
      {fields.length > 0 && (
        <header className="jira-custom-fields__header">
          <JiraProjectSettingsCustomFieldsHeaderItem
            heading="Field ID"
            tooltip="Found at the end of the URL when editing a custom field details at your Jira site (Settings > Issues > Custom Fields)"
          />
          <JiraProjectSettingsCustomFieldsHeaderItem
            heading="Field Display Name"
            tooltip="Name of the field when exporting an issue in FOSSA. Tip: Use the same name as in Jira to minimize confusion."
          />
          <JiraProjectSettingsCustomFieldsHeaderItem
            heading="Default Value (optional)"
            tooltip="User-editable value that will appear in this field. If left blank, the field will be empty."
          />
          <JiraProjectSettingsCustomFieldsHeaderItem
            heading="Required"
            tooltip="User can't export an issue to Jira until this field has a value."
          />
          <JiraProjectSettingsCustomFieldsHeaderItem heading="Delete" />
        </header>
      )}
      {fields.map(({ fieldId, displayName, defaultValue, isRequired }, index) => (
        //  Disabling this rule, since fieldId can change, which re-renders the row and the input loses focus.
        //  Since these rows don't change order, this should be okay.
        //  eslint-disable-next-line react/jsx-key
        <JiraProjectSettingsCustomFieldsRow
          fieldId={fieldId}
          displayName={displayName}
          defaultValue={defaultValue}
          isRequired={isRequired}
          isDisabled={isDisabled}
          onDelete={getOnDeleteCustomField(index)}
          onUpdateField={getOnUpdateField(index)}
        />
      ))}
      <footer className="jira-custom-fields__footer">
        <Button
          type="secondary"
          size="medium"
          onClick={onAddNewCustomField}
          isDisabled={isDisabled}
        >
          <IconText
            icon={icons.plus}
            text="Add Field"
          />
        </Button>
      </footer>
    </div>
  );
};

export default JiraCustomFields;
